/**
 * Create System Wizard
 * A guided, step-by-step flow for creating a new AI System.
 * Steps: Name & Purpose → Settings
 */

import React, { useState, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
  Chip,
  Collapse,
  InputAdornment,
  FormControlLabel,
  Switch,
  Paper,
  Stack,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { aiSystemApi } from '../../services/api';
import type { AISystem, AISystemCreate, AISystemListItem } from '../../types';
import { normalizeTags, rankTemplatesBySimilarity } from '../../utils/templateSearch';

// ───────────────── Wizard Steps ─────────────────

const STEPS = ['Name & Purpose', 'Settings'];
const TEMPLATE_PAGE_SIZE = 12;

type AccountReferenceSummary = {
  total_replacements: number;
  changed_paths?: Record<string, number>;
};

type CreateSystemMutationPayload = {
  data: AISystemCreate;
  sourceTemplateId?: number;
};

type CreateSystemMutationResult = AISystem & {
  account_reference_summary?: AccountReferenceSummary | null;
};

// ───────────────── Component ─────────────────

interface CreateSystemWizardProps {
  open: boolean;
  onClose: () => void;
}

export const CreateSystemWizard: React.FC<CreateSystemWizardProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateTagFilters, setTemplateTagFilters] = useState<string[]>([]);
  const [templateGalleryOpen, setTemplateGalleryOpen] = useState(false);
  const [selectedTemplateSnapshot, setSelectedTemplateSnapshot] = useState<AISystemListItem | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [requireApproval, setRequireApproval] = useState(true);
  const isLastStep = activeStep === STEPS.length - 1;

  const templatesQuery = useInfiniteQuery({
    queryKey: [
      'ai-system-templates',
      'create-wizard',
      templateSearch.trim(),
      templateTagFilters.join(','),
    ],
    queryFn: ({ pageParam }) =>
      aiSystemApi.templates({
        page: pageParam,
        page_size: TEMPLATE_PAGE_SIZE,
        ordering: 'name',
        search: templateSearch.trim() || undefined,
        tags: templateTagFilters.length ? templateTagFilters.join(',') : undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
    enabled: open && templateGalleryOpen,
    staleTime: 60_000,
  });

  const templateResults = useMemo(
    () => templatesQuery.data?.pages.flatMap((page) => page.results) || [],
    [templatesQuery.data?.pages],
  );

  const selectedTemplate = useMemo(() => {
    return templateResults.find((template) => template.id === templateId) || selectedTemplateSnapshot;
  }, [selectedTemplateSnapshot, templateId, templateResults]);

  const rankedTemplates = useMemo(
    () => rankTemplatesBySimilarity(templateResults, templateSearch),
    [templateResults, templateSearch],
  );

  const templateTagOptions = useMemo(() => {
    const counts = new Map<string, number>();
    templateResults.forEach((template) => {
      (template.tags || []).forEach((tag) => {
        counts.set(tag, (counts.get(tag) || 0) + 1);
      });
    });

    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag]) => tag)
      .slice(0, 12);
  }, [templateResults]);

  const visibleTemplates = useMemo(() => {
    const baseTemplates = templateSearch.trim()
      ? rankedTemplates
      : templateResults;

    if (!templateTagFilters.length) {
      return baseTemplates;
    }

    return baseTemplates.filter((template) => {
      const templateTags = new Set((template.tags || []).map((tag) => tag.toLowerCase()));
      return templateTagFilters.every((tag) => templateTags.has(tag.toLowerCase()));
    });
  }, [rankedTemplates, templateResults, templateSearch, templateTagFilters]);

  // Reset on open
  const handleClose = () => {
    setActiveStep(0);
    setName('');
    setDescription('');
    setTags([]);
    setTemplateId('');
    setTemplateSearch('');
    setTemplateTagFilters([]);
    setTemplateGalleryOpen(false);
    setSelectedTemplateSnapshot(null);
    setIsActive(false);
    setRequireApproval(true);
    onClose();
  };

  // Create mutation
  const createMutation = useMutation<CreateSystemMutationResult, any, CreateSystemMutationPayload>({
    mutationFn: async ({ data, sourceTemplateId }) => {
      if (!sourceTemplateId) {
        return aiSystemApi.create(data);
      }

      const cloneResponse = await aiSystemApi.clone(sourceTemplateId, data.name);
      const clonedSystem = cloneResponse.ai_system;
      const updatedSystem = await aiSystemApi.update(clonedSystem.id, {
        description: data.description,
        tags: data.tags,
        is_active: data.is_active,
        config: {
          ...(clonedSystem.config || {}),
          ...(data.config || {}),
        },
      });

      return {
        ...updatedSystem,
        account_reference_summary: cloneResponse.account_reference_summary,
      };
    },
    onSuccess: (createdSystem) => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      const removedAccounts = createdSystem.account_reference_summary?.total_replacements ?? 0;
      if (removedAccounts > 0) {
        toast.info('Template account IDs were cleared. Choose target account(s) before running.');
      }
      toast.success('System created! Opening the builder…');
      handleClose();
      navigate(`/ai/systems/${createdSystem.id}`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to create system');
    },
  });

  // Validation per step
  const canProceed = useMemo(() => {
    switch (activeStep) {
      case 0:
        return name.trim().length > 0;
      case 1:
        return true; // settings are optional
      default:
        return false;
    }
  }, [activeStep, name]);

  const handleTemplateSelect = (value: number | '') => {
    setTemplateId(value);

    if (!value) {
      setSelectedTemplateSnapshot(null);
      return;
    }

    const template = templateResults.find((item) => item.id === value);
    if (!template) {
      return;
    }

    setSelectedTemplateSnapshot(template);

    if (!name.trim()) {
      setName(`${template.name} Copy`);
    }

    if (!description.trim() && template.description) {
      setDescription(template.description);
    }

    if (!tags.length && template.tags?.length) {
      setTags(normalizeTags(template.tags));
    }
  };

  const toggleTemplateTagFilter = (tag: string) => {
    setTemplateTagFilters((current) =>
      current.includes(tag)
        ? current.filter((item) => item !== tag)
        : [...current, tag]
    );
  };

  const handleTemplateGalleryScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (
      distanceToBottom < 96 &&
      templatesQuery.hasNextPage &&
      !templatesQuery.isFetchingNextPage
    ) {
      templatesQuery.fetchNextPage();
    }
  };

  const handleCreate = () => {
    createMutation.mutate({
      data: {
        name: name.trim(),
        description: description.trim(),
        tags: normalizeTags(tags),
        is_active: isActive,
        config: {
          require_approval: requireApproval,
          initial_builder_mode: 'ai',
        },
      },
      sourceTemplateId: templateId || undefined,
    });
  };

  // Navigation
  const handleNext = () => {
    if (isLastStep) {
      handleCreate();
      return;
    }

    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // ───────────── Step Renderers ─────────────

  const renderNameStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
        Name & purpose
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Give it a clear name so you can find it later. The description helps the AI assistant understand what you're building.
      </Typography>

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ px: 2, py: 1.75 }}>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Start from template
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setTemplateGalleryOpen((value) => !value)}
                endIcon={templateGalleryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {templateGalleryOpen ? 'Hide templates' : 'Browse templates'}
              </Button>
            </Stack>

            <Paper variant="outlined" sx={{ px: 1.25, py: 1, bgcolor: 'action.hover' }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Box minWidth={0}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedTemplate ? selectedTemplate.name : 'Blank system'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {selectedTemplate?.description || 'No template selected.'}
                  </Typography>
                </Box>
                {selectedTemplate && (
                  <Button size="small" onClick={() => handleTemplateSelect('')}>
                    Clear
                  </Button>
                )}
              </Stack>
            </Paper>

            <Collapse in={templateGalleryOpen} unmountOnExit>
              <Stack spacing={1.5}>
            <TextField
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              fullWidth
              size="small"
              placeholder="Search templates by tag, strategy, market, or description"
              disabled={templatesQuery.isLoading}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            {!!templateTagOptions.length && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                {templateTagOptions.map((tag) => {
                  const selected = templateTagFilters.includes(tag);
                  return (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      clickable
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => toggleTemplateTagFilter(tag)}
                    />
                  );
                })}
              </Stack>
            )}

            {templatesQuery.isError && (
              <Typography variant="caption" color="error">
                Templates could not be loaded.
              </Typography>
            )}

            {templatesQuery.isLoading ? (
              <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading templates
                </Typography>
              </Box>
            ) : (
              <Box
                onScroll={handleTemplateGalleryScroll}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1,
                  maxHeight: { xs: 360, sm: 420 },
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                <Paper
                  component="button"
                  type="button"
                  variant="outlined"
                  onClick={() => handleTemplateSelect('')}
                  sx={{
                    p: 1.5,
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderColor: templateId === '' ? 'primary.main' : 'divider',
                    bgcolor: templateId === '' ? 'action.selected' : 'background.paper',
                    color: 'text.primary',
                  }}
                >
                  <Stack spacing={0.75}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Blank system
                      </Typography>
                      {templateId === '' && <CheckCircleIcon color="primary" fontSize="small" />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Create a new workflow without copying experts or settings.
                    </Typography>
                  </Stack>
                </Paper>

                {visibleTemplates.map((template) => {
                  const selected = template.id === templateId;
                  return (
                    <Paper
                      key={template.id}
                      component="button"
                      type="button"
                      variant="outlined"
                      onClick={() => handleTemplateSelect(template.id)}
                      sx={{
                        p: 1.5,
                        minHeight: 132,
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'action.selected' : 'background.paper',
                        color: 'text.primary',
                        transition: 'border-color 120ms ease, background-color 120ms ease',
                        '&:hover': {
                          borderColor: selected ? 'primary.main' : 'text.secondary',
                          bgcolor: selected ? 'action.selected' : 'action.hover',
                        },
                      }}
                    >
                      <Stack spacing={0.75} sx={{ height: '100%' }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                            {template.name}
                          </Typography>
                          {selected && <CheckCircleIcon color="primary" fontSize="small" />}
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {template.description || 'No description added.'}
                        </Typography>
                        {!!template.tags?.length && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 'auto', rowGap: 0.5 }}>
                            {template.is_public && (
                              <Chip label="Public" size="small" color="info" variant="outlined" sx={{ height: 20 }} />
                            )}
                            {template.tags.slice(0, 6).map((tag) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 20 }} />
                            ))}
                          </Stack>
                        )}
                        {!template.tags?.length && template.is_public && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 'auto', rowGap: 0.5 }}>
                            <Chip label="Public" size="small" color="info" variant="outlined" sx={{ height: 20 }} />
                          </Stack>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            )}

            {!templatesQuery.isLoading && visibleTemplates.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No templates match the current search or tag filters.
              </Typography>
            )}
            {templatesQuery.isFetchingNextPage && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Loading more templates
                </Typography>
              </Box>
            )}
              </Stack>
            </Collapse>
          </Stack>
        </Paper>
        <TextField
          label="System name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          autoFocus
          error={activeStep === 0 && !name.trim()}
          helperText={!name.trim() ? 'Required — e.g. "GBP/USD Day Trader"' : ''}
          placeholder="e.g. Day Trading GBP/USD"
        />
        <TextField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={3}
          maxRows={5}
          fullWidth
          placeholder="What does this system do? What markets, timeframes, or strategies does it cover?"
        />
        <Autocomplete<string, true, false, true>
          multiple
          freeSolo
          options={[]}
          value={tags}
          onChange={(_, value) => setTags(normalizeTags(value))}
          renderTags={(value, getTagProps) =>
            value.map((tag, index) => (
              <Chip
                {...getTagProps({ index })}
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tags"
              placeholder="Add tag"
              helperText="Use tags to find templates by market, strategy, timeframe, or workflow."
            />
          )}
        />
      </Stack>
    </Box>
  );

  const renderSettingsStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
        Key settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These can always be changed from the gear icon in the builder.
      </Typography>

      <Stack spacing={2.5} sx={{ maxWidth: 480 }}>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
          <FormControlLabel
            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Active</Typography>
                <Typography variant="caption" color="text.secondary">
                  When active, scheduled runs will fire. Leave off until the system is ready.
                </Typography>
              </Box>
            }
            labelPlacement="start"
          />
        </Paper>

        <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
          <FormControlLabel
            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            control={<Switch checked={requireApproval} onChange={(e) => setRequireApproval(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Trade Approval</Typography>
                <Typography variant="caption" color="text.secondary">
                  Require manual approval before action experts can place trades.
                </Typography>
              </Box>
            }
            labelPlacement="start"
          />
        </Paper>
      </Stack>
    </Box>
  );

  // Step content map
  const stepContent = [renderNameStep, renderSettingsStep];

  // ───────────── Main Dialog ─────────────

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          minHeight: isMobile ? '100%' : 520,
          maxHeight: isMobile ? '100%' : '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          New AI System
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Stepper */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1, flexShrink: 0 }}>
        <Stepper activeStep={activeStep} alternativeLabel={!isMobile}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{isMobile ? '' : label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, py: 2 }}>
        {stepContent[activeStep]()}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || createMutation.isPending}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!canProceed || createMutation.isPending}
          startIcon={
            isLastStep && createMutation.isPending ? (
              <CircularProgress color="inherit" size={16} />
            ) : undefined
          }
          endIcon={!isLastStep ? <NextIcon /> : undefined}
        >
          {isLastStep ? (createMutation.isPending ? 'Creating...' : 'Create System') : 'Next'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CreateSystemWizard;
